import {
  ForgotPasswordRequest,
  ResetPasswordRequest,
  VerifyEmailRequest,
  ChangePasswordRequest,
  SigninRequest,
  SigninResponse,
  SignupRequest,
  AzureConfigResponse,
  AzureLoginRequest,
  AzureLoginResponse,
  AzureStartRequest,
  AzureStartResponse,
  AuthSessionResponse,
  AuthSessionPayload,
  AuthActionResponse
} from '../types/auth';
import api from './api';
import axios from 'axios';
import { API_ENDPOINTS } from '../constants/api';

function normalizeAuthError(data: unknown): AuthActionResponse {
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    return {
      message: typeof obj.message === 'string' ? obj.message : 'Request failed',
      success: typeof obj.success === 'boolean' ? obj.success : false,
      data: obj.data && typeof obj.data === 'object' ? obj.data as Record<string, unknown> : undefined,
    };
  }
  return { success: false, message: 'Request failed' };
}

export async function signin(signinRequest: SigninRequest): Promise<SigninResponse> {
  try {
    const response = await api.request<SigninResponse>({
      method: 'post',
      url: API_ENDPOINTS.SIGNIN,
      data: signinRequest,
      timeout: 30000 // 30 second timeout
    });
    return response.data;
  } catch (error: any) {
    return error?.response?.data ?? { message: "Network error" };
  }
}

interface SignupResponse {
  success?: boolean;
  message?: string;
}

function normalizeSignupResponse(payload: unknown): SignupResponse {
  if (payload && typeof payload === 'object') {
    const asObject = payload as Record<string, unknown>;
    const rawSuccess = asObject.success;
    const rawMessage = asObject.message;

    return {
      ...(asObject as Record<string, unknown>),
      success: typeof rawSuccess === 'string' ? rawSuccess.toLowerCase() === 'true' : !!rawSuccess,
      message: typeof rawMessage === 'string' ? rawMessage : undefined
    };
  }

  return {
    success: false,
    message: 'Unexpected server response'
  };
}

export async function signup(signupRequest: SignupRequest): Promise<SignupResponse> {
  try {
    const response = await api.request<SignupResponse>({
      method: 'post',
      url: API_ENDPOINTS.SIGNUP,
      data: signupRequest,
      timeout: 30000 // 30 second timeout
    });
    return normalizeSignupResponse(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return normalizeSignupResponse(error.response?.data);
    }

    return {
      success: false,
      message: error instanceof Error ? error.message : 'Network error'
    };
  }
}

export async function forgotPassword(
  forgotPasswordRequest: ForgotPasswordRequest
): Promise<AuthActionResponse> {
  try {
    const response = await api.request<AuthActionResponse>({
      method: 'post',
      url: API_ENDPOINTS.FORGOT_PASSWORD,
      data: forgotPasswordRequest,
      timeout: 30000
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return normalizeAuthError(error.response.data);
    }
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Network error'
    };
  }
}

export async function verifyEmail(
  verifyEmailRequest: VerifyEmailRequest
): Promise<AuthActionResponse> {
  try {
    const response = await api.request<AuthActionResponse>({
      method: 'post',
      url: API_ENDPOINTS.VERIFY_EMAIL,
      data: verifyEmailRequest,
      timeout: 30000
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return normalizeAuthError(error.response.data);
    }
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Network error'
    };
  }
}

export async function resetPassword(
  resetPasswordRequest: ResetPasswordRequest
): Promise<AuthActionResponse> {
  try {
    const response = await api.request<AuthActionResponse>({
      method: 'patch',
      url: API_ENDPOINTS.RESET_PASSWORD,
      data: resetPasswordRequest,
      timeout: 30000
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return normalizeAuthError(error.response.data);
    }
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Network error'
    };
  }
}

export async function changePassword(
  changePasswordRequest: ChangePasswordRequest,
  userId: string
): Promise<AuthActionResponse> {
  try {
    const response = await api.request<AuthActionResponse>({
      method: 'patch',
      url: API_ENDPOINTS.CHANGE_PASSWORD(userId),
      data: changePasswordRequest,
      timeout: 30000
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return normalizeAuthError(error.response.data);
    }
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Network error'
    };
  }
}

/**
 * Whether Microsoft sign-in is turned on (and, given `redirectUri`, whether a
 * login may start with it). Rejects on failure so callers can hide the option.
 */
export async function getAzureConfig(redirectUri?: string): Promise<AzureConfigResponse> {
  const response = await api.request<AzureConfigResponse>({
    method: 'get',
    url: API_ENDPOINTS.AZURE_CONFIG,
    ...(redirectUri ? { params: { redirect_uri: redirectUri } } : {}),
    timeout: 30000
  });
  return { enabled: response.data?.enabled === true };
}

/** Begins a Microsoft sign-in; resolves with the authorize URL and the state to keep. */
export async function startAzureLogin(azureStartRequest: AzureStartRequest): Promise<AzureStartResponse> {
  try {
    const response = await api.request<AzureStartResponse>({
      method: 'post',
      url: API_ENDPOINTS.AZURE_START,
      data: azureStartRequest,
      timeout: 30000
    });
    const { authorize_url: authorizeUrl, state } = response.data ?? ({} as Partial<AzureStartResponse>);
    if (typeof authorizeUrl !== 'string' || !authorizeUrl || typeof state !== 'string' || !state) {
      throw new Error('Unexpected server response');
    }
    return { authorize_url: authorizeUrl, state };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      const data = error.response.data as Record<string, unknown>;
      const message = typeof data.detail === 'string' ? data.detail : typeof data.message === 'string' ? data.message : undefined;
      throw new Error(message || 'Microsoft sign-in is unavailable');
    }
    throw error instanceof Error ? error : new Error('Network error');
  }
}

export async function azureLogin(azureLoginRequest: AzureLoginRequest): Promise<AzureLoginResponse> {
  try {
    const response = await api.request<AzureLoginResponse>({
      method: 'post',
      url: API_ENDPOINTS.AZURE_LOGIN,
      data: azureLoginRequest,
      timeout: 30000 // 30 second timeout
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return error.response.data as AzureLoginResponse;
    }
    return { success: false, message: (error as Error).message || 'Network error' };
  }
}

export async function getAuthSession(): Promise<AuthSessionPayload | null> {
  try {
    const response = await api.request<AuthSessionResponse>({
      method: 'get',
      url: API_ENDPOINTS.AUTH_SESSION,
      timeout: 30000
    });

    const payload = response.data?.data;
    return payload && typeof payload.authenticated === 'boolean' ? payload : null;
  } catch {
    return null;
  }
}
